import React, { useEffect, useState } from 'react';
import { Box, Typography, CircularProgress, Button } from '@mui/material';
import useContentApi from '../../api/useContentApi';
import { ContentType, Content } from '../../types/content';
import { UserBase } from '../../types/user';
import { getZodiacSign, getZodiacHebrewName, getHoroscopeTypeText } from '../../services/zodiacService';
import { formatDate } from '../../utils/dateUtils';
import ZodiacDisplay from '../../components/ZodiacDisplay';
import NatalChartVisualizer from '../../components/AstrologicalChart';
import { HoroscopeState } from '.';

interface ReviewStepProps {
  state: HoroscopeState;
  userData: UserBase;
}
 
const ReviewStep: React.FC<ReviewStepProps> = ({ state, userData }) => {
  const [zodiacContent, setZodiacContent] = useState<Content | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [showNatalChart, setShowNatalChart] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const { getContentByType } = useContentApi();

  const userZodiac = getZodiacSign(userData?.birth_date);
  const zodiacHebrewName = getZodiacHebrewName(userZodiac);

  useEffect(() => {
    const fetchZodiacContent = async () => {
      if (!userZodiac) return;

      try {
        setLoading(true);
        const contents = await getContentByType(ContentType.ZODIAC);
        // Filter contents for the user's zodiac sign
        const zodiacSpecificContents = contents.filter(content =>
          content.key.toLowerCase().includes(userZodiac.toLowerCase())
        );

        if (zodiacSpecificContents.length > 0) {
          setZodiacContent(zodiacSpecificContents[0]); // Take the first matching content
        }
        setError('');
      } catch (err) {
        console.error('Failed to fetch zodiac content:', err);
        setError('לא ניתן לטעון את תוכן המזל. אנא נסה שוב מאוחר יותר.');
      } finally {
        setLoading(false);
      }
    };

    fetchZodiacContent();
  }, [getContentByType, userZodiac]);

  return (
    <Box>
      <Typography variant="subtitle1">
        <strong>שם מלא:</strong> {userData?.first_name} {userData?.last_name}
      </Typography>
      <Typography variant="subtitle1">
        <strong>תאריך לידה:</strong> {formatDate(userData?.birth_date)}
      </Typography>

      <Typography variant="subtitle1">
        <strong>סוג הורוסקופ:</strong> {getHoroscopeTypeText(state.horoscopeType)}
      </Typography>
      <Typography variant="subtitle1">
        <strong>תחומי עניין:</strong> {state.selectedInterests.join(', ')}
      </Typography>

      <Box display="flex" justifyContent="center" mt={3}>
        <Button variant="contained" color="primary" onClick={() => setShowNatalChart(!showNatalChart)}>
          {showNatalChart ? 'הסתר תרשימ מוליך' : 'הצג תרשימ מוליך'}
        </Button>
      </Box>
      {showNatalChart && <NatalChartVisualizer userId={userData.id || ''} size="50%" />}

      {loading ? (
        <Box display="flex" justifyContent="center" mt={3}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Typography color="error" mt={2}>{error}</Typography>
      ) : zodiacContent ? (
        <Box mt={3} p={2}  borderRadius={1}>
          <Typography variant="h6" gutterBottom>
            {zodiacContent.title || `תחזית למזל ${zodiacHebrewName}`}
          </Typography>
          <ZodiacDisplay
            zodiacSign={userZodiac}
            zodiacName=""
            textPrefix=""
            imageSize={16}
            gap={0}
          />
          <Typography variant="body1"  >
            {zodiacContent.content}
          </Typography>

        </Box>
      ) : (
        <Typography variant="body1" sx={{ mt: 2 }}>
          לחץ על "סיום" כדי לשלוח את הבקשה.
        </Typography>
      )}
    </Box>
  );
};

export default ReviewStep; 